import React, { useRef, useState, useEffect } from 'react';

const SHORT_LABELS = {
    'infra': 'Infraestrutura',
    'comercial': 'Comercial',
    'churn': 'Retenção',
    'qualidade': 'Qualidade'
};

/**
 * Componente de navegação em abas para a Dashboard.
 * Em telas móveis, previne transbordamento utilizando scroll horizontal com fade indicators
 * e exibe rótulos mais compactos.
 */
export default function DashboardSubTabs({ tabs, activeTab, onTabChange }) {
    const scrollRef = useRef(null);
    const tabRefs = useRef({});
    const [showLeftFade, setShowLeftFade] = useState(false);
    const [showRightFade, setShowRightFade] = useState(false);

    const checkScroll = () => {
        const el = scrollRef.current;
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        setShowLeftFade(scrollLeft > 4);
        setShowRightFade(scrollLeft < scrollWidth - clientWidth - 4);
    };

    useEffect(() => {
        checkScroll();
        window.addEventListener('resize', checkScroll);
        return () => window.removeEventListener('resize', checkScroll);
    }, [tabs]);

    useEffect(() => {
        // Centraliza a guia ativa suavemente
        if (activeTab && tabRefs.current[activeTab]) {
            tabRefs.current[activeTab].scrollIntoView({
                behavior: 'smooth',
                inline: 'center',
                block: 'nearest'
            });
        }
        checkScroll();
    }, [activeTab]);

    return (
        <div className="relative border-b border-slate-200 dark:border-slate-700 mb-6">
            {/* Indicador de fade à esquerda */}
            <div
                className={`pointer-events-none absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white dark:from-slate-800 to-transparent z-10 transition-opacity duration-300 ${
                    showLeftFade ? 'opacity-100' : 'opacity-0'
                }`}
                aria-hidden="true"
            />

            {/* Container com rolagem horizontal oculta */}
            <div
                ref={scrollRef}
                onScroll={checkScroll}
                className="flex overflow-x-auto scrollbar-hide flex-nowrap -mb-px px-1 sm:px-0"
                role="tablist"
                aria-label="Guias da Dashboard"
            >
                {tabs.map((tab) => {
                    const isActive = activeTab === tab.id;
                    const shortLabel = SHORT_LABELS[tab.id] || tab.label;

                    return (
                        <button
                            key={tab.id}
                            ref={(el) => (tabRefs.current[tab.id] = el)}
                            role="tab"
                            aria-selected={isActive}
                            onClick={() => onTabChange(tab.id)}
                            className={`whitespace-nowrap py-2.5 px-3 sm:px-4 font-semibold text-xs sm:text-sm outline-none transition-colors shrink-0 ${
                                isActive
                                    ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
                                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300 border-b-2 border-transparent'
                            }`}
                        >
                            <span className="sm:hidden">{shortLabel}</span>
                            <span className="hidden sm:inline">{tab.label}</span>
                        </button>
                    );
                })}
            </div>

            {/* Indicador de fade à direita */}
            <div
                className={`pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white dark:from-slate-800 to-transparent z-10 transition-opacity duration-300 ${
                    showRightFade ? 'opacity-100' : 'opacity-0'
                }`}
                aria-hidden="true"
            />
        </div>
    );
}
